import { UseMutationResult } from '@tanstack/react-query';
import { reconfigureReport } from './reconfigure';
import { useSession } from '../SessionProvider';
import { ReportOverlayResponse } from './getReportResult';
import { useTrackedMutation as useMutation } from '@ais-ui/performance-tracking';

interface DensityMutationParams {
  jobId: string;
  colorScale: Record<string, any>;
  numberOfColors: number;
  smoothing: number;
  minShips: number;
  maxShips?: number;
}

type FetchOverlayData = (
  jobId: string,
  hasZoomed: boolean
) => Promise<ReportOverlayResponse | null>;

export const useDensityReconfigure = (
  fetchOverlayData: FetchOverlayData,
  minMaxShipsThreshold: { min: number; max: number },
  setMinMaxShipsThreshold: React.Dispatch<
    React.SetStateAction<{ min: number; max: number }>
  >,
  setMinMaxShips: React.Dispatch<
    React.SetStateAction<{ min: number; max: number }>
  >
): UseMutationResult<string, Error, DensityMutationParams> => {
  const { dbSession } = useSession();

  return useMutation<string, Error, DensityMutationParams>(
    ({ jobId, colorScale, numberOfColors, smoothing, minShips, maxShips }) => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }

      const params: Record<string, unknown> = {
        SM: smoothing,
        CD: numberOfColors,
        SMML: 1, // This has to be to set in order for the other SMMLs to work
        SMMLMIN: minShips,
        CS: colorScale,
      };

      if (maxShips) {
        params['SMMLMAX'] = maxShips;
      }

      return reconfigureReport(dbSession, jobId, params).then((result) =>
        String(result)
      );
    },
    {
      onSuccess: async (data, variables) => {
        const overlay = await fetchOverlayData(variables.jobId, true);

        // Ideally the information about total number of ships should come directly in the report, but the
        //  backend doesn't send it. So we've made this hack where we set the report to use max number of ships
        //  and collect the information from the legend.
        const maxNumberOfShips = overlay?.legend?.items?.length
          ? overlay.legend.items[overlay.legend.items.length - 1]?.end_interval
          : undefined;
        if (maxNumberOfShips) {
          setMinMaxShips((prevState) => ({
            min: prevState.min,
            max: maxNumberOfShips,
          }));
        }
        if (maxNumberOfShips && minMaxShipsThreshold.max < maxNumberOfShips) {
          setMinMaxShipsThreshold((prevState) => ({
            min: prevState.min,
            max: maxNumberOfShips,
          }));
        }
      },
    }
  );
};

interface TrackMutationParams {
  jobId: string;
  colorBy: string;
  colorIntervals: any;
}

export const useTrackReconfigure = (
  fetchOverlayData: FetchOverlayData
): UseMutationResult<string, Error, TrackMutationParams> => {
  const { dbSession } = useSession();

  return useMutation<string, Error, TrackMutationParams>(
    ({ jobId, colorBy, colorIntervals }) => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }

      const params = {
        CB: colorBy,
        CBCD: JSON.stringify(colorIntervals),
      };

      return reconfigureReport(dbSession, jobId, params).then((result) =>
        String(result)
      );
    },
    {
      onSuccess: (data, variables) => {
        fetchOverlayData(variables.jobId, true);
      },
    }
  );
};

interface MultiShapeMutationParams {
  jobId: string;
  outputType: string;
  statisticGrouping: string;
  interval: string;
}

export const useMultiShapeReconfigure = (
  fetchOverlayData: FetchOverlayData
): UseMutationResult<string, Error, MultiShapeMutationParams> => {
  const { dbSession } = useSession();

  return useMutation<string, Error, MultiShapeMutationParams>(
    ({ jobId, outputType, statisticGrouping, interval }) => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }

      const params = {
        PAC: outputType,
        STG: statisticGrouping,
        TYP: 'table',
        PI: interval,
      };

      return reconfigureReport(dbSession, jobId, params).then((result) =>
        String(result)
      );
    },
    {
      onSuccess: (data, variables) => {
        fetchOverlayData(variables.jobId, true);
      },
    }
  );
};

interface PassagelineMutationParams {
  jobId: string;
  outputType: string;
}

export const usePassagelineReconfigure = (
  fetchOverlayData: FetchOverlayData
): UseMutationResult<string, Error, PassagelineMutationParams> => {
  const { dbSession } = useSession();

  return useMutation<string, Error, PassagelineMutationParams>(
    ({ jobId, outputType }) => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }

      const params = {
        TYP: outputType,
      };

      return reconfigureReport(dbSession, jobId, params).then((result) =>
        String(result)
      );
    },
    {
      onSuccess: (data, variables) => {
        fetchOverlayData(variables.jobId, true);
      },
    }
  );
};

interface SpeedOverGroundMutationParams {
  jobId: string;
  statisticGrouping: string;
  interval: string;
}

export const useSpeedOverGroundReconfigure = (
  fetchOverlayData: FetchOverlayData
): UseMutationResult<string, Error, SpeedOverGroundMutationParams> => {
  const { dbSession } = useSession();

  return useMutation<string, Error, SpeedOverGroundMutationParams>(
    ({ jobId, statisticGrouping, interval }) => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }

      const params = {
        STG: statisticGrouping,
        PI: interval,
      };

      return reconfigureReport(dbSession, jobId, params).then((result) =>
        String(result)
      );
    },
    {
      onSuccess: (data, variables) => {
        fetchOverlayData(variables.jobId, true);
      },
    }
  );
};

interface CoverageMutationParams {
  jobId: string;
  numberOfColors: number;
}

export const useCoverageReconfigure = (
  fetchOverlayData: FetchOverlayData
): UseMutationResult<string, Error, CoverageMutationParams> => {
  const { dbSession } = useSession();

  return useMutation<string, Error, CoverageMutationParams>(
    ({ jobId, numberOfColors }) => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }

      const params = {
        CD: numberOfColors,
      };

      return reconfigureReport(dbSession, jobId, params).then((result) =>
        String(result)
      );
    },
    {
      onSuccess: (data, variables) => {
        fetchOverlayData(variables.jobId, true);
      },
    }
  );
};

interface PassagelineShipsMutationParams {
  jobId: string;
  outputType: string;
  statisticGrouping: string;
  interval: string;
}

export const usePassagelineShipsReconfigure = (
  fetchOverlayData: FetchOverlayData
): UseMutationResult<string, Error, PassagelineShipsMutationParams> => {
  const { dbSession } = useSession();

  return useMutation<string, Error, PassagelineShipsMutationParams>(
    ({ jobId, outputType, statisticGrouping, interval }) => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }

      const params = {
        PAC: outputType,
        STG: statisticGrouping,
        TYP: 'table',
        PI: interval,
      };

      return reconfigureReport(dbSession, jobId, params).then((result) =>
        String(result)
      );
    },
    {
      onSuccess: (data, variables) => {
        fetchOverlayData(variables.jobId, true);
      },
    }
  );
};
